<script setup>
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  sellers: {
    type: Array,
    default: () => [],
  },
  agentMetrics: {
    type: Array,
    default: () => [],
  },
});

const store = useStore();
const dialogRef = ref(null);
const deactivateDialogRef = ref(null);
const deleteDialogRef = ref(null);
const sellerFormDialogRef = ref(null);
const syncingId = ref(null);
const deactivatingId = ref(null);
const reactivatingId = ref(null);
const deletingId = ref(null);
const savingSeller = ref(false);
const sellerPendingDeactivation = ref(null);
const sellerPendingDeletion = ref(null);
const sellerForm = ref({
  id: null,
  name: '',
  email: '',
});

const accountId = () => {
  if (window.location.pathname.includes('/app/accounts')) {
    return window.location.pathname.split('/')[3];
  }

  return '';
};

const managementSellers = computed(
  () => store.getters['agents/getOfficialSellerManagement'] || []
);

const open = () => {
  dialogRef.value?.open();
  store.dispatch('agents/getOfficialSellerManagement');
};
const close = () => dialogRef.value?.close();

const getAgentMetrics = id =>
  props.agentMetrics.find(metrics => metrics.assignee_id === Number(id)) || {};

const statusLabel = seller => {
  if (!seller.active) return 'Inativo';

  const labels = {
    linked: 'Oficial',
    missing_aceleres_user: 'Pendente Aceleres',
    missing_chatwoot_user: 'Sem Chatwoot',
    missing_chatwoot_account_user: 'Sem conta Chatwoot',
  };

  return labels[seller.sync_status] || 'Verificar';
};

const statusClass = seller => {
  if (!seller.active) return 'bg-n-slate-3 text-n-slate-11';
  if (seller.sync_status === 'linked') return 'bg-n-teal-3 text-n-teal-11';
  if (seller.sync_status === 'missing_aceleres_user') {
    return 'bg-n-amber-3 text-n-amber-11';
  }

  return 'bg-n-ruby-3 text-n-ruby-11';
};

const sourceLabel = seller =>
  seller.source === 'chatwoot' ? 'Chatwoot' : 'Aceleres';

const canSync = seller =>
  seller.sync_status === 'missing_aceleres_user' && Number(seller.id) > 0;

const canDeactivate = seller =>
  seller.sync_status === 'linked' && seller.active && Number(seller.id) > 0;

const sellerActionId = seller =>
  seller.id || seller.aceleres_user_id || seller.email;

const canReactivate = seller =>
  (Number(seller.id) > 0 || Number(seller.aceleres_user_id) > 0) &&
  (!seller.active || seller.sync_status === 'missing_chatwoot_account_user');

const reactivateLabel = seller =>
  seller.active && seller.sync_status === 'missing_chatwoot_account_user'
    ? 'Corrigir vínculo'
    : 'Reativar';

const canDeleteLocal = seller =>
  !seller.active &&
  Number(seller.aceleres_user_id) > 0 &&
  !Number(seller.id) &&
  !Number(seller.chatwoot_user_id);

const canEdit = seller =>
  seller.sync_status === 'linked' && seller.active && Number(seller.id) > 0;

const rows = computed(() =>
  (managementSellers.value.length ? managementSellers.value : props.sellers)
    .map(seller => {
      const metrics = getAgentMetrics(seller.id);
      return {
        ...seller,
        open: metrics.open || 0,
        unattended: metrics.unattended || 0,
        displayName: seller.available_name || seller.name,
      };
    })
    .sort((a, b) => {
      const activeDiff = Number(b.active) - Number(a.active);
      if (activeDiff !== 0) return activeDiff;

      const statusDiff = Number(a.sync_status === 'linked') - Number(b.sync_status === 'linked');
      if (statusDiff !== 0) return statusDiff;

      const openDiff = b.open - a.open;
      if (openDiff !== 0) return openDiff;

      return a.displayName.localeCompare(b.displayName);
    })
);

const statusSummary = computed(() => {
  const inactive = rows.value.filter(row => !row.active).length;
  const pending = rows.value.filter(
    row => row.active && row.sync_status !== 'linked'
  ).length;
  const official = rows.value.filter(
    row => row.active && row.sync_status === 'linked'
  ).length;

  return {
    total: rows.value.length,
    official,
    pending,
    inactive,
  };
});

const isEditingSeller = computed(() => Number(sellerForm.value.id) > 0);

const sellerFormTitle = computed(() =>
  isEditingSeller.value ? 'Editar vendedor' : 'Cadastrar vendedor'
);

const resetSellerForm = () => {
  sellerForm.value = {
    id: null,
    name: '',
    email: '',
  };
};

const openCreateSeller = () => {
  resetSellerForm();
  sellerFormDialogRef.value?.open();
};

const openEditSeller = seller => {
  if (!canEdit(seller)) return;

  sellerForm.value = {
    id: seller.id,
    name: seller.displayName || seller.name || '',
    email: seller.email || '',
  };
  sellerFormDialogRef.value?.open();
};

const closeSellerFormDialog = () => {
  resetSellerForm();
  sellerFormDialogRef.value?.close();
};

const saveSeller = async () => {
  const payload = {
    name: sellerForm.value.name.trim(),
    available_name: sellerForm.value.name.trim(),
    email: sellerForm.value.email.trim().toLowerCase(),
  };

  if (!payload.name || !payload.email) {
    useAlert('Informe nome e e-mail do vendedor.');
    return;
  }

  savingSeller.value = true;

  try {
    if (isEditingSeller.value) {
      await store.dispatch('agents/updateOfficialSeller', {
        chatwootUserId: sellerForm.value.id,
        seller: payload,
      });
      useAlert('Vendedor atualizado.');
    } else {
      await store.dispatch('agents/createOfficialSeller', payload);
      useAlert('Vendedor cadastrado e sincronizado.');
    }
    closeSellerFormDialog();
  } catch (error) {
    const apiError = error?.response?.data?.error;
    const messages = {
      email_already_used: 'Este e-mail já está vinculado a outro usuário.',
      email_already_used_by_other_company: 'Este e-mail pertence a outra empresa.',
    };
    useAlert(messages[apiError] || 'Não foi possível salvar o vendedor.');
  } finally {
    savingSeller.value = false;
  }
};

const syncSeller = async seller => {
  if (!canSync(seller)) return;

  syncingId.value = seller.id;

  try {
    await store.dispatch('agents/syncOfficialSeller', seller.id);
    useAlert('Vendedor sincronizado com o Aceleres.');
  } catch (error) {
    useAlert('Não foi possível sincronizar o vendedor.');
  } finally {
    syncingId.value = null;
  }
};

const requestDeactivateSeller = seller => {
  if (!canDeactivate(seller)) return;

  sellerPendingDeactivation.value = seller;
  deactivateDialogRef.value?.open();
};

const closeDeactivateDialog = () => {
  sellerPendingDeactivation.value = null;
  deactivateDialogRef.value?.close();
};

const deactivateSeller = async () => {
  const seller = sellerPendingDeactivation.value;
  if (!seller || !canDeactivate(seller)) return;

  deactivatingId.value = seller.id;

  try {
    await store.dispatch('agents/deactivateOfficialSeller', seller.id);
    useAlert('Vendedor desativado e removido da conta no Chatwoot.');
    closeDeactivateDialog();
  } catch (error) {
    useAlert('Não foi possível desativar o vendedor.');
  } finally {
    deactivatingId.value = null;
  }
};

const reactivateSeller = async seller => {
  if (!canReactivate(seller)) return;

  reactivatingId.value = sellerActionId(seller);

  try {
    await store.dispatch('agents/reactivateOfficialSeller', {
      chatwootUserId: seller.id,
      aceleresUserId: seller.aceleres_user_id,
    });
    useAlert(
      seller.active
        ? 'Vínculo com o Chatwoot corrigido.'
        : 'Vendedor reativado e sincronizado com o Chatwoot.'
    );
  } catch (error) {
    useAlert('Não foi possível reativar o vendedor.');
  } finally {
    reactivatingId.value = null;
  }
};

const requestDeleteSeller = seller => {
  if (!canDeleteLocal(seller)) return;

  sellerPendingDeletion.value = seller;
  deleteDialogRef.value?.open();
};

const closeDeleteDialog = () => {
  sellerPendingDeletion.value = null;
  deleteDialogRef.value?.close();
};

const deleteSeller = async () => {
  const seller = sellerPendingDeletion.value;
  if (!seller || !canDeleteLocal(seller)) return;

  deletingId.value = sellerActionId(seller);

  try {
    await store.dispatch(
      'agents/deleteLocalOfficialSeller',
      seller.aceleres_user_id
    );
    useAlert('Vendedor excluído do gerenciamento local.');
    closeDeleteDialog();
  } catch (error) {
    const apiError = error?.response?.data?.error;
    const messages = {
      seller_must_be_inactive: 'Desative o vendedor antes de excluir.',
      linked_seller_deletion_not_allowed:
        'Este vendedor ainda possui vínculo com o Chatwoot. Use Desativar.',
    };
    useAlert(messages[apiError] || 'Não foi possível excluir o vendedor.');
  } finally {
    deletingId.value = null;
  }
};

const openDetails = seller => {
  const id = accountId();
  const url = id
    ? `/app/accounts/${id}/settings/agents/list`
    : '/app/accounts';

  window.open(url, '_blank', 'noopener,noreferrer');
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="6xl"
    title="Gerenciar vendedores"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
  >
    <div class="flex flex-col gap-4">
      <div class="grid grid-cols-4 gap-3">
        <div class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2">
          <p class="mb-1 text-xs text-n-slate-11">Total</p>
          <p class="mb-0 text-lg font-medium text-n-slate-12">
            {{ statusSummary.total }}
          </p>
        </div>
        <div class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2">
          <p class="mb-1 text-xs text-n-slate-11">Oficiais</p>
          <p class="mb-0 text-lg font-medium text-n-teal-11">
            {{ statusSummary.official }}
          </p>
        </div>
        <div class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2">
          <p class="mb-1 text-xs text-n-slate-11">Pendentes</p>
          <p class="mb-0 text-lg font-medium text-n-amber-11">
            {{ statusSummary.pending }}
          </p>
        </div>
        <div class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2">
          <p class="mb-1 text-xs text-n-slate-11">Inativos</p>
          <p class="mb-0 text-lg font-medium text-n-slate-11">
            {{ statusSummary.inactive }}
          </p>
        </div>
      </div>

      <div class="flex justify-end">
        <Button
          label="Novo vendedor"
          icon="i-lucide-user-plus"
          size="sm"
          color="blue"
          variant="faded"
          type="button"
          @click="openCreateSeller"
        />
      </div>

      <div class="max-h-[28rem] overflow-y-auto overflow-x-hidden rounded-md border border-n-weak">
        <table class="w-full table-fixed border-collapse text-left text-sm">
          <thead class="sticky top-0 z-10 bg-n-solid-2 text-xs text-n-slate-11">
            <tr>
              <th class="w-[34%] px-3 py-2 font-medium">Vendedor</th>
              <th class="w-[11%] px-3 py-2 font-medium">Origem</th>
              <th class="w-[13%] px-3 py-2 font-medium">Status</th>
              <th class="w-[9%] px-3 py-2 text-right font-medium">Abertas</th>
              <th class="w-[13%] px-3 py-2 text-right font-medium">Não atendidas</th>
              <th class="w-[20%] px-3 py-2 text-right font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="seller in rows"
              :key="seller.id || seller.email"
              class="border-t border-n-weak"
            >
              <td class="px-3 py-2">
                <div class="flex items-center gap-2">
                  <Avatar
                    :src="seller.thumbnail"
                    :name="seller.displayName"
                    :status="seller.availability_status"
                    :size="28"
                    hide-offline-status
                    rounded-full
                  />
                  <div class="min-w-0">
                    <p
                      class="mb-0 truncate text-sm font-medium text-n-slate-12"
                    >
                      {{ seller.displayName }}
                    </p>
                    <p class="mb-0 truncate text-xs text-n-slate-11">
                      {{ seller.email }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ sourceLabel(seller) }}
              </td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex rounded-sm px-2 py-1 text-xs font-medium leading-none"
                  :class="statusClass(seller)"
                >
                  {{ statusLabel(seller) }}
                </span>
              </td>
              <td class="px-3 py-2 text-right text-n-slate-12">
                {{ seller.open }}
              </td>
              <td class="px-3 py-2 text-right text-n-slate-12">
                {{ seller.unattended }}
              </td>
              <td class="px-3 py-2">
                <div class="flex flex-wrap justify-end gap-2">
                  <Button
                    v-if="canSync(seller)"
                    label="Sincronizar"
                    icon="i-lucide-refresh-cw"
                    size="xs"
                    color="amber"
                    variant="faded"
                    :is-loading="syncingId === seller.id"
                    :disabled="syncingId === seller.id"
                    type="button"
                    @click="syncSeller(seller)"
                  />
                  <Button
                    v-if="canDeactivate(seller)"
                    label="Desativar"
                    icon="i-lucide-user-x"
                    size="xs"
                    color="ruby"
                    variant="faded"
                    :is-loading="deactivatingId === seller.id"
                    :disabled="deactivatingId === seller.id"
                    type="button"
                    @click="requestDeactivateSeller(seller)"
                  />
                  <Button
                    v-if="canReactivate(seller)"
                    :label="reactivateLabel(seller)"
                    icon="i-lucide-user-check"
                    size="xs"
                    color="teal"
                    variant="faded"
                    :is-loading="reactivatingId === sellerActionId(seller)"
                    :disabled="reactivatingId === sellerActionId(seller)"
                    type="button"
                    @click="reactivateSeller(seller)"
                  />
                  <Button
                    v-if="canDeleteLocal(seller)"
                    label="Excluir"
                    icon="i-lucide-trash-2"
                    size="xs"
                    color="ruby"
                    variant="ghost"
                    :is-loading="deletingId === sellerActionId(seller)"
                    :disabled="deletingId === sellerActionId(seller)"
                    type="button"
                    @click="requestDeleteSeller(seller)"
                  />
                  <Button
                    v-if="canEdit(seller)"
                    label="Editar"
                    icon="i-lucide-pencil"
                    size="xs"
                    color="slate"
                    variant="faded"
                    type="button"
                    @click="openEditSeller(seller)"
                  />
                  <Button
                    label="Detalhes"
                    icon="i-lucide-external-link"
                    size="xs"
                    color="slate"
                    variant="ghost"
                    type="button"
                    @click="openDetails(seller)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <Button
          label="Fechar"
          color="slate"
          variant="faded"
          type="button"
          @click="close"
        />
      </div>
    </template>
  </Dialog>

  <Dialog
    ref="sellerFormDialogRef"
    type="edit"
    width="md"
    :title="sellerFormTitle"
    confirm-button-label="Salvar"
    cancel-button-label="Cancelar"
    :is-loading="savingSeller"
    :disable-confirm-button="savingSeller"
    @confirm="saveSeller"
    @close="resetSellerForm"
  >
    <div class="flex flex-col gap-4">
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">Nome</span>
        <input
          v-model="sellerForm.name"
          class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
          type="text"
          autocomplete="name"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">E-mail</span>
        <input
          v-model="sellerForm.email"
          class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
          type="email"
          autocomplete="email"
        />
      </label>
    </div>
  </Dialog>

  <Dialog
    ref="deactivateDialogRef"
    type="alert"
    width="md"
    title="Desativar vendedor"
    confirm-button-label="Desativar"
    cancel-button-label="Cancelar"
    :is-loading="deactivatingId === sellerPendingDeactivation?.id"
    :disable-confirm-button="deactivatingId === sellerPendingDeactivation?.id"
    @confirm="deactivateSeller"
    @close="sellerPendingDeactivation = null"
  >
    <p class="mb-0 text-sm text-n-slate-11">
      {{ sellerPendingDeactivation?.displayName }} será desativado no Aceleres
      e removido desta conta no Chatwoot. O histórico de conversas será
      preservado.
    </p>
  </Dialog>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    width="md"
    title="Excluir vendedor"
    confirm-button-label="Excluir"
    cancel-button-label="Cancelar"
    :is-loading="deletingId === sellerActionId(sellerPendingDeletion || {})"
    :disable-confirm-button="deletingId === sellerActionId(sellerPendingDeletion || {})"
    @confirm="deleteSeller"
    @close="sellerPendingDeletion = null"
  >
    <p class="mb-0 text-sm text-n-slate-11">
      {{ sellerPendingDeletion?.displayName }} será removido do gerenciamento
      local do Aceleres. Esta ação só é permitida para vendedores inativos sem
      vínculo com o Chatwoot.
    </p>
  </Dialog>
</template>
